'use client'

import { useMutation, useQuery } from '@apollo/client'
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { ANALYTICS_EVENTS, ANALYTICS_PAGES } from '../constants/analytics'
import { AUTH_ROUTES, USER_ROUTES } from '../constants/routes'
import { useAnalytics } from '../contexts/analytics-context'
import { CREATE_USERNAME, UPDATE_USER } from '../data/mutations/auth'
import { VALIDATE_USERNAME_UNIQUENESS } from '../data/queries/auth'
import { useDebounce } from '../hooks/use-debounce'
import { useUserQuery } from '../hooks/use-user-query'
import { tokenUtils } from '../utils/token-utils'

export const generateUsername = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim()
}

export const usernameFormSchema = z.object({
  name: z.string().min(1, 'Name is required').min(2, 'Name must be at least 2 characters')
})

type UsernameFormValues = z.infer<typeof usernameFormSchema>

type ValidationStatus = 'idle' | 'validating' | 'available' | 'unavailable'

/**
 * Displays the username status message
 * @param generatedUsername - The generated username
 * @param validationStatus - The validation status
 * @param finalUsername - The final username
 * @returns
 */
const UserNameStatusMessage = ({
  generatedUsername,
  validationStatus,
  finalUsername
}: {
  generatedUsername: string
  validationStatus: ValidationStatus
  finalUsername: string
}) => {
  // Show preview even for single characters, but indicate it needs more
  if (generatedUsername && generatedUsername.length === 1) {
    return (
      <div className="text-xs sm:text-sm text-muted-foreground px-1 py-1.5 sm:py-2">
        <p className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <span>Your username will be:</span>
          <span className="font-medium text-foreground">{generatedUsername}</span>
          <span className="text-xs opacity-75">(Please enter at least 2 characters)</span>
        </p>
      </div>
    )
  }

  if (!generatedUsername) return null

  switch (validationStatus) {
    case 'validating':
      return (
        <div className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 px-1 py-1.5 sm:py-2">
          <span className="loading loading-spinner loading-xs" />
          <span>Checking username availability...</span>
        </div>
      )
    case 'available':
      // Show message - if username has suffix, indicate original was taken
      if (finalUsername.includes('-')) {
        const baseUsername = finalUsername.split('-')[0]
        return (
          <div className="text-xs sm:text-sm text-green-600 dark:text-green-400 px-1 py-1.5 sm:py-2 rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
            <p className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
              <span>
                <span className="text-amber-600 dark:text-amber-400 font-medium">{baseUsername}</span> is not available.
              </span>
              <span>
                Available username: <span className="font-semibold">{finalUsername}</span>
              </span>
            </p>
          </div>
        )
      }
      return (
        <div className="text-xs sm:text-sm text-green-600 dark:text-green-400 px-1 py-1.5 sm:py-2 rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
          <p className="flex items-center gap-2">
            <span className="text-base">✓</span>
            <span>
              Username <span className="font-semibold">{finalUsername}</span> is available and ready to use!
            </span>
          </p>
        </div>
      )
    case 'unavailable':
      return finalUsername ? (
        <div className="text-xs sm:text-sm text-amber-600 dark:text-amber-400 px-1 py-1.5 sm:py-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
          <p className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
            <span>Username is already taken.</span>
            <span>
              Available alternative: <span className="font-semibold">{finalUsername}</span>
            </span>
          </p>
        </div>
      ) : null
    default:
      return (
        <div className="text-xs sm:text-sm text-muted-foreground px-1 py-1.5 sm:py-2">
          <p className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span>Your username will be:</span>
            <span className="font-medium text-foreground">{generatedUsername}</span>
          </p>
        </div>
      )
  }
}

export const UserNameForm = ({ isCarrierOnboarding = false }: { isCarrierOnboarding?: boolean }) => {
  const { setUserProperty, captureEvent } = useAnalytics()
  const router = useRouter()
  const isAuthenticated = tokenUtils.isAuthenticated()
  const { loading: loadingUserDetails, myDetails } = useUserQuery()

  // Separate generated username from final validated username
  const [generatedUsername, setGeneratedUsername] = useState('')
  const [finalUsername, setFinalUsername] = useState('')
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('idle')
  const [usernameToValidate, setUsernameToValidate] = useState<string | null>(null)
  const debouncedGeneratedUsername = useDebounce(generatedUsername, 500)
  const lastValidatedRef = useRef<string>('')

  // Redirect if already active
  useEffect(() => {
    if (isAuthenticated && myDetails?.status === 'Active' && !isCarrierOnboarding) {
      router.replace(USER_ROUTES.SHIPMENTS)
    }
  }, [isAuthenticated, myDetails?.status, isCarrierOnboarding])

  // Form setup
  const form = useForm({
    defaultValues: {
      name: ''
    },
    resolver: zodResolver(usernameFormSchema),
    mode: 'onChange' // Validate on change to show errors immediately
  })

  const [candidateToCheck, setCandidateToCheck] = useState<string | null>(null)
  const currentSuffixRef = useRef(1)
  const baseUsernameForSuffixRef = useRef<string>('')

  // Query for base username validation
  const { loading: validatingBase } = useQuery(VALIDATE_USERNAME_UNIQUENESS, {
    skip: !usernameToValidate || usernameToValidate === lastValidatedRef.current || candidateToCheck !== null,
    variables: { constraint: usernameToValidate || '' },
    onCompleted: (data: { getConstraint: { id: string } | null }) => {
      const { getConstraint: constraint } = data
      const baseUsername = usernameToValidate || ''

      if (!constraint) {
        // Base username is available
        setValidationStatus('available')
        setFinalUsername(baseUsername)
        lastValidatedRef.current = baseUsername
        setUsernameToValidate(null)
      } else {
        // Base username is taken, start checking candidates
        // Don't set status to unavailable yet - wait until we find an available one
        setValidationStatus('validating')
        baseUsernameForSuffixRef.current = baseUsername
        currentSuffixRef.current = 1
        const firstCandidate = `${baseUsername}-1`
        setCandidateToCheck(firstCandidate)
      }
    },
    onError: () => {
      setValidationStatus('idle')
      setUsernameToValidate(null)
      toast.error('Failed to validate username. Please try again.')
    },
    fetchPolicy: 'network-only'
  })

  // Query for candidate username validation (only when base is taken)
  const { loading: validatingCandidate } = useQuery(VALIDATE_USERNAME_UNIQUENESS, {
    skip: !candidateToCheck,
    variables: { constraint: candidateToCheck || '' },
    onCompleted: (data: { getConstraint: { id: string } | null }) => {
      const { getConstraint: constraint } = data
      const candidate = candidateToCheck || ''

      if (!constraint) {
        // Found available username with suffix
        setValidationStatus('available')
        setFinalUsername(candidate)
        lastValidatedRef.current = baseUsernameForSuffixRef.current
        setCandidateToCheck(null)
        setUsernameToValidate(null)
        // Don't update the input - keep the original name
        // Just update generatedUsername for display purposes
        setGeneratedUsername(candidate)
      } else {
        // This candidate is also taken, try next one
        // Keep status as 'validating' while searching
        currentSuffixRef.current += 1
        if (currentSuffixRef.current > 100) {
          setValidationStatus('idle')
          setFinalUsername('')
          setCandidateToCheck(null)
          setUsernameToValidate(null)
          toast.error('Could not find an available username. Please try a different name.')
          return
        }
        const nextCandidate = `${baseUsernameForSuffixRef.current}-${currentSuffixRef.current}`
        setCandidateToCheck(nextCandidate)
      }
    },
    onError: () => {
      setValidationStatus('idle')
      setCandidateToCheck(null)
      setUsernameToValidate(null)
      toast.error('Failed to validate username. Please try again.')
    },
    fetchPolicy: 'network-only'
  })

  const validating = validatingBase || validatingCandidate

  // Validate username when debounced value changes (user stopped typing)
  useEffect(() => {
    if (!debouncedGeneratedUsername || debouncedGeneratedUsername.length < 2) {
      setValidationStatus('idle')
      setFinalUsername('')
      setUsernameToValidate(null)
      setCandidateToCheck(null)
      lastValidatedRef.current = ''
      currentSuffixRef.current = 1
      baseUsernameForSuffixRef.current = ''
      return
    }

    // Only validate if this is a new username (different from last validated)
    if (debouncedGeneratedUsername !== lastValidatedRef.current) {
      setValidationStatus('validating')
      setCandidateToCheck(null) // Reset candidate checking
      currentSuffixRef.current = 1
      setUsernameToValidate(debouncedGeneratedUsername)
    }
  }, [debouncedGeneratedUsername])

  const [createUsername, { loading: creating }] = useMutation(CREATE_USERNAME, {
    onCompleted: () => {
      toast.success('Your username has been created successfully')
      router.replace(AUTH_ROUTES.PROFILE_PICTURE)
    },
    onError: () => {
      toast.error('Failed to create username. Please try again.')
    }
  })

  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER, {
    onError: () => {
      toast.error('Failed to update user. Please try again.')
    }
  })

  const handleNameChange = (value: string) => {
    if (!value.trim()) {
      setGeneratedUsername('')
      setFinalUsername('')
      setValidationStatus('idle')
      return
    }
    const generated = generateUsername(value)
    setGeneratedUsername(generated)
  }

  const onSubmit = useCallback(
    async (values: UsernameFormValues) => {
      if (!finalUsername || validationStatus !== 'available') {
        toast.error('Please wait for username validation to complete')
        return
      }

      const validatedUsername = finalUsername

      try {
        setUserProperty({ user_name: values.name })
        captureEvent(ANALYTICS_EVENTS.auth.userNameSubmit, { from_page: ANALYTICS_PAGES.userName })

        // Update user first
        await updateUser({
          variables: {
            colorModePreference: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
            name: values.name
          }
        })

        // Then create username with the validated username
        await createUsername({ variables: { username: validatedUsername } })
      } catch (error) {
        console.error('Submission error:', error)
        toast.error('Failed to update user details')
      }
    },
    [updateUser, createUsername, finalUsername, validationStatus, setUserProperty, captureEvent]
  )

  if (loadingUserDetails) {
    return (
      <div className="flex items-center justify-center">
        <span className="loading loading-spinner loading-md" />
      </div>
    )
  }

  const isLoading = updating || creating
  const isFormDisabled = isLoading || validating

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    handleNameChange(e.target.value)
                    // Trigger validation on change
                    form.trigger('name')
                  }}
                  onBlur={() => {
                    field.onBlur()
                    form.trigger('name')
                  }}
                  disabled={isFormDisabled}
                  placeholder="Enter your full name"
                  className="w-full"
                  aria-label="Full name input"
                  initialFocus={true}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <UserNameStatusMessage
          generatedUsername={generatedUsername}
          validationStatus={validationStatus}
          finalUsername={finalUsername}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={isFormDisabled || !finalUsername || validationStatus !== 'available'}
          data-testid="continue-button"
          aria-label={isLoading ? 'Processing...' : 'Continue with username creation'}
        >
          {isLoading ? (
            <>
              <span className="loading loading-spinner loading-sm mr-2" />
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </form>
    </Form>
  )
}
